import { z } from "zod";
import { PHASE_HISTORY_STATUS_TYPES, FOLLOW_UP_STATUS_TYPES } from "../constants/workflow-phase-consts";
import { AgentSnapshotSchema, WorkflowSnapshotSchema, PhasesSnapshotSchema } from "./follow-up-snapshots";

export const FollowUpReferenceDatesSchema = z.object({
  treatmentStart: z.string().optional(),
  hospitalDischarge: z.string().optional(),
  surgeryDate: z.string().optional(),
});

// ============================================================================
// PHASE HISTORY SCHEMA
// ============================================================================

export const PhaseHistoryEntrySchema = z.object({
  phaseId: z.string(),
  phaseIndex: z.number(),
  phaseName: z.string(),
  startedAt: z.string(),
  completedAt: z.string().nullable().optional(),
  pausedAt: z.string().nullable().optional(),
  resumedAt: z.string().nullable().optional(),
  cancelledAt: z.string().nullable().optional(),
  skippedAt: z.string().nullable().optional(),
  pauseReason: z.string().nullable().optional(),
  cancellationReason: z.string().nullable().optional(),
  skipReason: z.string().nullable().optional(),
  duration: z.number().nullable().optional(),
  nextCallAt: z.string().nullable().optional(),
  callsAttempted: z.number().nullable().optional(),
  callsCompleted: z.number().nullable().optional(),
  callsFailedCount: z.number().nullable().optional(),
  dataCollected: z.record(z.string(), z.unknown()).nullable().optional(),
  alertsTriggered: z
    .array(
      z.object({
        alertId: z.string(),
        severity: z.string(),
        triggeredAt: z.string(),
        resolved: z.boolean(),
      }),
    )
    .nullable()
    .optional(),
  status: z.enum(PHASE_HISTORY_STATUS_TYPES).nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
});

// ============================================================================
// BASE FOLLOW-UP SCHEMA (matches backend DbFollowUpOutput)
// ============================================================================

// To sync with DbFollowUpOutput in backend/schemas/follow-up.ts
const BaseFollowUpSchema = z.object({
  // Primary fields
  id: z.string(),

  // Relations
  patientId: z.string(),
  agentId: z.string(),
  workflowId: z.string(),
  creatorId: z.string(),

  // Immutable snapshots for RGPD traceability (3 separate snapshots)
  agentSnapshot: AgentSnapshotSchema, // Agent configuration only
  workflowSnapshot: WorkflowSnapshotSchema, // Workflow metadata only
  phasesSnapshot: PhasesSnapshotSchema, // Array of all phases

  // Current follow-up state
  currentPhaseId: z.string().nullable().optional(),
  currentPhaseIndex: z.number().nullable().optional(),
  currentPhaseStartedAt: z.string().nullable().optional(),

  // Phase progression history for RGPD traceability
  phaseHistory: z.array(PhaseHistoryEntrySchema).nullable().optional(),

  status: z.enum(FOLLOW_UP_STATUS_TYPES).nullable().optional(),

  // Timestamps
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  startedAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  pausedAt: z.string().nullable().optional(),
  cancelledAt: z.string().nullable().optional(),
  cancellationReason: z.string().nullable().optional(),

  // Supervision fields
  nextCheckDate: z.string().nullable().optional(),
  priorityScore: z.number().nullable().optional(),
  lastCheckedAt: z.string().nullable().optional(),

  // Data fields
  notes: z.string().nullable().optional(),
  tags: z.array(z.string()).nullable().optional(),
  authConfig: z.record(z.string(), z.unknown()).nullable().optional(),
  pendingActions: z.record(z.string(), z.unknown()).nullable().optional(),
  resourceConstraints: z.record(z.string(), z.unknown()).nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  isVirtual: z.boolean().default(false),
});

export const FollowUpSchema = BaseFollowUpSchema;

export const FollowUpListItemSchema = BaseFollowUpSchema.extend({
  patientFirstName: z.string().nullable().optional(),
  patientLastName: z.string().nullable().optional(),
  patientPhone: z.string().nullable().optional(),
  patientEmail: z.string().nullable().optional(),
  agentName: z.string().nullable().optional(),
  creatorFirstName: z.string().nullable().optional(),
  creatorLastName: z.string().nullable().optional(),
  creatorEmail: z.string().nullable().optional(),
});

// Follow-up with full patient data (for detail page)
export const FollowUpWithPatientSchema = BaseFollowUpSchema.extend({
  patientFirstName: z.string().nullable().optional(),
  patientLastName: z.string().nullable().optional(),
  patientPhone: z.string().nullable().optional(),
  patientEmail: z.string().nullable().optional(),
  patientDateOfBirth: z.string().nullable().optional(),
  patientExternalId: z.string().nullable().optional(),
  patientWorkspaceId: z.string().nullable().optional(),
  patientCreatedAt: z.string().nullable().optional(),
  patientArchivedAt: z.string().nullable().optional(),
});

export const FollowUpListSchema = z.array(FollowUpListItemSchema);

// ============================================================================
// CREATE/UPDATE PAYLOAD SCHEMAS (camelCase for API)
// ============================================================================

export const CreateFollowUpBodySchema = z.object({
  userId: z.string().uuid("Invalid user ID"),
  patientId: z.string().uuid("Invalid patient ID"),
  agentId: z.string().uuid("Invalid agent ID"),
  tags: z.array(z.string()).optional(),
  notes: z.string().optional(),
  priorityScore: z.number().optional(),
  authConfig: z.record(z.string(), z.any()).optional(),
  metadata: z.record(z.string(), z.any()).optional(),
  startDate: z.string().optional(),
  referenceDates: FollowUpReferenceDatesSchema.optional(),
});

export const UpdateFollowUpBodySchema = z.object({
  status: z.enum(FOLLOW_UP_STATUS_TYPES).nullable().optional(),
  currentPhaseId: z.string().uuid().optional(),
  currentPhaseIndex: z.number().optional(),
  notes: z.string().optional(),
  tags: z.array(z.string()).optional(),
  priorityScore: z.number().optional(),
  authConfig: z.record(z.string(), z.any()).nullable().optional(),
  pendingActions: z.record(z.string(), z.any()).optional(),
  resourceConstraints: z.record(z.string(), z.any()).optional(),
  metadata: z.record(z.string(), z.any()).optional(),
});

export const CancelFollowUpBodySchema = z.object({
  cancellationReason: z.string().min(1, "Cancellation reason is required"),
});

// ============================================================================
// SUPERVISOR OVERRIDES SCHEMA
// ============================================================================

export const FollowUpSupervisorOverridesSchema = z
  .object({
    // Retry rules override
    retryRules: z
      .object({
        maxAttemptsPerSession: z.number().min(1).max(10).optional(),
        retryDelayMinutes: z.number().min(1).optional(),
      })
      .optional(),

    // Priority override
    priority: z.number().min(0).max(100).optional(),

    // Operating hours override (can override global operating hours)
    operatingHours: z
      .object({
        enabled: z.boolean().optional(),
        timezone: z.string().optional(),
        windows: z
          .array(
            z.object({
              dayOfWeek: z.array(z.number().min(0).max(6)),
              startTime: z.string().regex(/^\d{2}:\d{2}$/),
              endTime: z.string().regex(/^\d{2}:\d{2}$/),
            }),
          )
          .optional(),
      })
      .optional(),
  })
  .optional();

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type CreateFollowUpBody = z.infer<typeof CreateFollowUpBodySchema>;
export type UpdateFollowUpBody = z.infer<typeof UpdateFollowUpBodySchema>;
export type CancelFollowUpBody = z.infer<typeof CancelFollowUpBodySchema>;

// API Response types
export type CreateFollowUpApiResponse = FollowUp;
export type UpdateFollowUpApiResponse = FollowUp;
export type FollowUpListApiResponse = z.infer<typeof FollowUpListSchema>;

export type FollowUp = z.infer<typeof FollowUpSchema>;
export type FollowUpListItem = z.infer<typeof FollowUpListItemSchema>;
export type FollowUpWithPatient = z.infer<typeof FollowUpWithPatientSchema>;
export type PhaseHistoryEntry = z.infer<typeof PhaseHistoryEntrySchema>;
export type FollowUpSupervisorOverrides = z.infer<typeof FollowUpSupervisorOverridesSchema>;
